import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, MenuItem, Paper, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { Delete, Edit, WarningAmber } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context";
import { createCTScanRecord, fetchCTScanRecords, fetchCTScanTests, updateCTScanRecord, deleteCTScanRecord } from "../../../api/ct_scan";
import axios from "axios";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000/api").replace(/\/$/, "");
const MEDIA_BASE = (process.env.REACT_APP_MEDIA_URL || API.replace(/\/api$/, "")).replace(/\/$/, "");
const REFUND_STORAGE_KEY = "reception_refunds";

const toAbsoluteMediaUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `${MEDIA_BASE}${value.startsWith("/") ? value : `/${value}`}`;
};

const toDateTimeLocal = (dateValue, timeValue) => {
  if (!dateValue) return "";
  const safeTime = (timeValue || "00:00").slice(0, 5);
  return `${dateValue}T${safeTime}`;
};

const fromDateTimeLocal = (value) => {
  if (!value) return { date: "", time: "" };
  const [datePart, timePart] = String(value).split("T");
  return { date: datePart || "", time: (timePart || "").slice(0, 5) };
};

const format12HourTime = (timeValue) => {
  if (!timeValue) return "-";
  const raw = String(timeValue).slice(0, 5);
  const [hStr, mStr] = raw.split(":");
  const h = Number(hStr);
  const m = Number(mStr);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return raw;
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(hour12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
};

const formInputSx = {
  "& .MuiInputBase-input": {
    py: 1.45,
    px: 1.75,
  },
  "& .MuiInputBase-inputMultiline": {
    py: 1.2,
    px: 1.75,
  },
};

const PatientCTScanRecordsPage = () => {
  const { t } = useTranslation();
  const token = useAuth()?.user?.token;
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [tests, setTests] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openAdd, setOpenAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [editStatus, setEditStatus] = useState("Pending");
  const [originalEdit, setOriginalEdit] = useState(null);
  const [ticketSearch, setTicketSearch] = useState("");
  const [ticketOpen, setTicketOpen] = useState(false);
  const [form, setForm] = useState({
    patient: "",
    test: "",
    ticket: "",
    test_result: "",
    notes: "",
    date: new Date().toISOString().slice(0, 10),
    record_time: new Date().toTimeString().slice(0, 5),
    report_image: null,
  });
  const [examDateTime, setExamDateTime] = useState(
    `${new Date().toISOString().slice(0, 10)}T${new Date().toTimeString().slice(0, 5)}`
  );

  const load = async () => {
    const headers = token ? { Authorization: `Token ${token}` } : {};
    const [rec, tst, tkt] = await Promise.all([
      fetchCTScanRecords(token),
      fetchCTScanTests(token),
      axios.get(`${API}/tickets/`, { headers }).then((r) => r.data),
    ]);
    setRecords(rec); setTests(tst); setTickets(tkt.results || tkt);
  };
  useEffect(() => { if (token) load(); }, [token]);

  const filtered = useMemo(() => records.filter((r) => {
    const q = query.toLowerCase();
    const matches = [r.ticket_display, r.patient_display?.id, r.patient_display?.name, r.test_display?.name, r.test_result, r.notes].join(" ").toLowerCase().includes(q);
    return statusFilter === "all" ? matches : matches && r.status === statusFilter;
  }), [records, query, statusFilter]);
  const selectedTicket = useMemo(
    () => tickets.find((tk) => String(tk.id) === String(form.ticket)),
    [tickets, form.ticket]
  );

  const handleCreate = async () => {
    if (!form.patient || !form.test || !form.date || !form.ticket) return;
    const parsedDateTime = fromDateTimeLocal(examDateTime);
    const finalDate = parsedDateTime.date || form.date;
    const finalTime = parsedDateTime.time || form.record_time || "00:00";
    setSaving(true);
    setFormError("");
    try {
      const hasFile = typeof File !== "undefined" && form.report_image instanceof File;
      if (editId) {
        const payload = {
          test_result: form.test_result || "",
          notes: form.notes || "",
          date: finalDate,
          record_time: finalTime,
        };
        if (form.ticket) payload.ticket = form.ticket;
        if (editStatus && ["Pending", "In Review", "Completed", "Canceled"].includes(editStatus)) {
          payload.status = editStatus;
        }
        if (originalEdit && String(form.patient) !== String(originalEdit.patient || originalEdit.patient_display?.id || "")) {
          payload.patient = form.patient;
        }
        if (originalEdit && String(form.test) !== String(originalEdit.test || originalEdit.test_display?.id || "")) {
          payload.test = form.test;
        }
        if (hasFile) {
          const filePayload = new FormData();
          Object.entries(payload).forEach(([k, v]) => {
            if (v !== null && v !== undefined && v !== "") {
              filePayload.append(k, v);
            }
          });
          filePayload.append("report_image", form.report_image);
          await updateCTScanRecord(token, editId, filePayload);
        } else {
          await updateCTScanRecord(token, editId, payload);
        }
        setSnackbar({ open: true, message: t("ct_scan.patientCtScanRecordUpdatedSuccessfully", "Patient CT Scan record updated successfully!"), severity: "success" });
      } else {
        const selectedTest = tests.find((t) => String(t.id) === String(form.test));
        const testPrice = Number(selectedTest?.price ?? selectedTest?.amount ?? selectedTest?.cost ?? 0);
        const safeAmount = Number.isFinite(testPrice) ? testPrice : 0;
        const basePayload = {
          patient: Number(form.patient),
          test: Number(form.test),
          amount: safeAmount,
          total_amount: safeAmount,
          discount: 0,
          discount_amount: 0,
          test_result: form.test_result || "",
          notes: form.notes || "",
          date: finalDate,
          record_time: finalTime,
          status: "Pending",
        };
        if (form.ticket) basePayload.ticket = Number(form.ticket);

        if (hasFile) {
          const payload = new FormData();
          Object.entries(basePayload).forEach(([k, v]) => payload.append(k, v));
          payload.append("report_image", form.report_image);
          await createCTScanRecord(token, payload);
        } else {
          await createCTScanRecord(token, basePayload);
        }
        setSnackbar({ open: true, message: t("ct_scan.patientCtScanRecordAddedSuccessfully", "Patient CT Scan record added successfully!"), severity: "success" });
      }
      setOpenAdd(false);
      setEditId(null);
      setOriginalEdit(null);
        setForm({
        patient: "",
        test: "",
        ticket: "",
        test_result: "",
        notes: "",
        date: new Date().toISOString().slice(0, 10),
        record_time: new Date().toTimeString().slice(0, 5),
          report_image: null,
        });
        setExamDateTime(
          `${new Date().toISOString().slice(0, 10)}T${new Date().toTimeString().slice(0, 5)}`
        );
        setTicketSearch("");
        setEditStatus("Pending");
      await load();
    } catch (error) {
      const data = error?.response?.data;
      if (data && typeof data === "object") {
        const msg = Object.entries(data)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
          .join(" | ");
        setFormError(msg || t("ct_scan.failedToCreateRecord", "Failed to create record."));
      } else {
        setFormError(t("ct_scan.failedToCreateRecord", "Failed to create record."));
      }
      setSnackbar({ open: true, message: t("ct_scan.failedToSavePatientCtScanRecord", "Failed to save patient CT Scan record!"), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRequest = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    try {
      await deleteCTScanRecord(token, deleteId);
      try {
        const existing = JSON.parse(localStorage.getItem(REFUND_STORAGE_KEY) || "[]");
        const filtered = Array.isArray(existing)
          ? existing.filter((r) => {
              const type = String(r?.item_type || "");
              const sameItem = String(r?.item_id) === String(deleteId);
              const isCTScan = type === "ct_scan" || type === "ct_scan_record";
              return !(sameItem && isCTScan);
            })
          : [];
        localStorage.setItem(REFUND_STORAGE_KEY, JSON.stringify(filtered));
        window.dispatchEvent(new Event("reception-refunds-updated"));
      } catch {}
      setSnackbar({ open: true, message: t("ct_scan.patientCtScanRecordDeletedSuccessfully", "Patient CT Scan record deleted successfully!"), severity: "success" });
      setDeleteDialogOpen(false);
      setDeleteId(null);
      await load();
    } catch (e) {
      setSnackbar({ open: true, message: t("ct_scan.failedToDeletePatientCtScanRecord", "Failed to delete patient CT Scan record!"), severity: "error" });
    }
  };

  return (
    <>
      <Grid container sx={{ p: 2, maxWidth: 1024, mx: "auto" }}>
        <Grid item xs={12}>
          <Paper sx={{ width: "100%", p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              {t("ct_scan.addNewPatientCtScanRecord", "Add New Patient CT Scan Record")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("ct_scan.ticketNumber", "Ticket Number")}
                  placeholder={t("ct_scan.searchByTicketNumber", "Search by ticket number...")}
                  value={ticketSearch}
                  onFocus={() => setTicketOpen(true)}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTicketSearch(val);
                    setForm({ ...form, ticket: "", patient: "", test: "" });
                    setTicketOpen(true);
                  }}
                  helperText={
                    ticketSearch.length < 3
                      ? t("ct_scan.typeAtLeast3Chars", "Type at least 3 characters to search")
                      : !form.ticket
                        ? t("ct_scan.selectTicketFromResults", "Please select a ticket from results")
                        : " "
                  }
                  error={ticketSearch.length >= 3 && !form.ticket}
                  sx={formInputSx}
                />
                {ticketOpen && ticketSearch.length >= 3 && (
                  <Paper sx={{ mt: 1, maxHeight: 220, overflowY: "auto", border: "1px solid #ddd" }}>
                    {tickets
                      .filter((tk) =>
                        (tk.ticket_number || "").toLowerCase().includes(ticketSearch.toLowerCase())
                      )
                      .map((tk) => (
                        <MenuItem
                          key={tk.id}
                          onClick={() => {
                            const referralForTicket = [...records]
                              .filter((r) => String(r.ticket || "") === String(tk.id))
                              .sort((a, b) => Number(b.id) - Number(a.id))[0];
                            setTicketSearch(tk.ticket_number || "");
                            setForm({
                              ...form,
                              ticket: tk.id,
                              patient: tk.patient || "",
                              test: referralForTicket?.test || referralForTicket?.test_display?.id || "",
                            });
                            setTicketOpen(false);
                          }}
                        >
                          <Stack>
                            <Typography fontWeight={600}>{tk.ticket_number}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {tk.patient_name || `Patient ID: ${tk.patient || "-"}`}
                            </Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    {tickets.filter((tk) => (tk.ticket_number || "").toLowerCase().includes(ticketSearch.toLowerCase())).length === 0 && (
                      <Typography sx={{ p: 1.5, color: "text.secondary" }}>{t("ct_scan.noTicketsFound", "No tickets found")}</Typography>
                    )}
                  </Paper>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  required
                  label={t("ct_scan.patient", "Patient")}
                  value={form.patient}
                  onChange={(e) => setForm({ ...form, patient: e.target.value })}
                  disabled={!form.ticket}
                  sx={formInputSx}
                >
                  {selectedTicket?.patient ? (
                    <MenuItem value={selectedTicket.patient}>
                      {selectedTicket.patient_name || selectedTicket.patient_name_display || `Patient ID: ${selectedTicket.patient}`}
                    </MenuItem>
                  ) : (
                    <MenuItem value="" disabled>{t("ct_scan.noPatientFound", "No patient found")}</MenuItem>
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  required
                  label={t("ct_scan.patientCtScan", "Patient CT Scan")}
                  value={form.test}
                  onChange={(e) => setForm({ ...form, test: e.target.value })}
                  disabled={!form.ticket}
                  sx={formInputSx}
                >
                  {tests.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label={t("ct_scan.dateAndTime", "Date & Time")}
                  InputLabelProps={{ shrink: true }}
                  value={examDateTime}
                  onChange={(e) => {
                    setExamDateTime(e.target.value);
                    const dt = fromDateTimeLocal(e.target.value);
                    setForm({ ...form, date: dt.date, record_time: dt.time });
                  }}
                  sx={formInputSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Button variant="outlined" component="label" fullWidth>
                  {form.report_image instanceof File ? form.report_image.name : t("ct_scan.uploadPhotoReportImage", "Upload Photo / Report Image")}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => setForm({ ...form, report_image: e.target.files?.[0] || null })}
                  />
                </Button>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("ct_scan.testAppointment", "Test Appointment")}
                  value={form.test_result}
                  onChange={(e) => setForm({ ...form, test_result: e.target.value })}
                  sx={formInputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={3} label={t("ct_scan.notes", "Notes")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} sx={formInputSx} />
              </Grid>
              {formError ? <Grid item xs={12}><Typography color="error" variant="body2">{formError}</Typography></Grid> : null}
              <Grid item xs={12}>
                <Button variant="contained" onClick={handleCreate} disabled={saving || !form.patient || !form.test || !form.ticket}>
                  {saving ? t("ct_scan.saving", "Saving...") : t("ct_scan.addRecord", "Add Record")}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Divider sx={{ mb: 3 }} />

          <Paper sx={{ width: "100%", p: 3, borderRadius: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 3 }}>
              <TextField
                size="small"
                label={t("ct_scan.search", "Search")}
                placeholder={t("ct_scan.searchByTicketPatientTest", "Search by ticket, patient, test...")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ minWidth: 280 }}
              />
              <TextField
                size="small"
                select
                label={t("ct_scan.status", "Status")}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="all">{t("ct_scan.all", "All")}</MenuItem>
                <MenuItem value="Completed">{t("ct_scan.completed", "Completed")}</MenuItem>
                <MenuItem value="In Review">{t("ct_scan.inReview", "In Review")}</MenuItem>
                <MenuItem value="Pending">{t("ct_scan.pending", "Pending")}</MenuItem>
              </TextField>
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
                    <TableCell sx={{ fontWeight: 700 }}>{t("ct_scan.patientName", "Patient Name")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("ct_scan.testName", "Test Name")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("ct_scan.testPendings", "Test Pendings")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("ct_scan.notes", "Notes")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("ct_scan.photoReportImage", "Photo / Report Image")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("ct_scan.date", "Date")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("ct_scan.status", "Status")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">{t("ct_scan.actions", "Actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((row) => (
                  <TableRow key={row.id} hover sx={{ backgroundColor: "#e0f4f7" }}>
                    <TableCell>{row.patient_display?.name}</TableCell>
                    <TableCell>{row.test_display?.name}</TableCell>
                    <TableCell>{row.test_result || "-"}</TableCell>
                    <TableCell>{row.notes || "-"}</TableCell>
                    <TableCell>
                      {row.report_image ? (
                        <img
                          src={toAbsoluteMediaUrl(row.report_image)}
                          alt={t("ct_scan.testType", "Test Type")}
                          style={{
                            width: 48,
                            height: 48,
                            objectFit: "cover",
                            borderRadius: 6,
                            border: "1px solid #ddd",
                            cursor: "pointer",
                          }}
                          onClick={() => window.open(toAbsoluteMediaUrl(row.report_image), "_blank")}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{row.date}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={row.status === "Completed" ? "success" : row.status === "In Review" ? "warning" : "default"} />
                    </TableCell>
                    <TableCell align="right">
                                            <IconButton size="small" sx={{ color: "#0b5b7c" }} onClick={() => {
                        setEditId(row.id);
                        setOriginalEdit(row);
                        setForm({
                          patient: row.patient || row.patient_display?.id || "",
                          test: row.test || row.test_display?.id || "",
                          ticket: row.ticket || "",
                          test_result: row.test_result || "",
                          notes: row.notes || "",
                          date: row.date || new Date().toISOString().slice(0, 10),
                          record_time: row.record_time || new Date().toTimeString().slice(0, 5),
                          report_image: null,
                        });
                        setExamDateTime(
                          toDateTimeLocal(
                            row.date || new Date().toISOString().slice(0, 10),
                            row.record_time || new Date().toTimeString().slice(0, 5)
                          )
                        );
                        const matchedTicket = tickets.find((tk) => String(tk.id) === String(row.ticket));
                        setTicketSearch(matchedTicket?.ticket_number || row.ticket_display || "");
                        setEditStatus(row.status || "Pending");
                        setOpenAdd(true);
                      }}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" sx={{ color: "#e53935" }} onClick={() => handleDeleteRequest(row.id)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
      <Dialog open={openAdd} onClose={() => { setOpenAdd(false); setEditId(null); }} fullWidth maxWidth="md">
    <DialogTitle>{editId ? t("ct_scan.editPatientCtScanRecord", "Edit Patient CT Scan Record") : t("ct_scan.addNewPatientCtScanRecord", "Add Patient CT Scan Record")}</DialogTitle>
    <DialogContent>
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("ct_scan.ticketNumber", "Ticket Number")}
            placeholder={t("ct_scan.searchByTicketNumber", "Search by ticket number...")}
            value={ticketSearch}
            onFocus={() => setTicketOpen(true)}
            onChange={(e) => {
              const val = e.target.value;
              setTicketSearch(val);
              setForm({ ...form, ticket: "", patient: "", test: "" });
              setTicketOpen(true);
            }}
            helperText={
              ticketSearch.length < 3
                ? t("ct_scan.typeAtLeast3Chars", "Type at least 3 characters to search")
                : !form.ticket
                  ? t("ct_scan.selectTicketFromResults", "Please select a ticket from results")
                  : " "
            }
            error={ticketSearch.length >= 3 && !form.ticket}
          />
          {ticketOpen && ticketSearch.length >= 3 && (
            <Paper sx={{ mt: 1, maxHeight: 220, overflowY: "auto", border: "1px solid #ddd" }}>
              {tickets
                .filter((tk) =>
                  (tk.ticket_number || "").toLowerCase().includes(ticketSearch.toLowerCase())
                )
                .map((tk) => (
                  <MenuItem
                    key={tk.id}
                    onClick={() => {
                      const referralForTicket = [...records]
                        .filter((r) => String(r.ticket || "") === String(tk.id))
                        .sort((a, b) => Number(b.id) - Number(a.id))[0];
                      setTicketSearch(tk.ticket_number || "");
                      setForm({
                        ...form,
                        ticket: tk.id,
                        patient: tk.patient || "",
                        test: referralForTicket?.test || referralForTicket?.test_display?.id || "",
                      });
                      setTicketOpen(false);
                    }}
                  >
                    <Stack>
                      <Typography fontWeight={600}>{tk.ticket_number}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {tk.patient_name || `Patient ID: ${tk.patient || "-"}`}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              {tickets.filter((tk) => (tk.ticket_number || "").toLowerCase().includes(ticketSearch.toLowerCase())).length === 0 && (
                <Typography sx={{ p: 1.5, color: "text.secondary" }}>{t("ct_scan.noTicketsFound", "No tickets found")}</Typography>
              )}
            </Paper>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            required
            label={t("ct_scan.patient", "Patient")}
            value={form.patient}
            onChange={(e) => setForm({ ...form, patient: e.target.value })}
            disabled={!form.ticket}
          >
            {selectedTicket?.patient ? (
              <MenuItem value={selectedTicket.patient}>
                {selectedTicket.patient_name || selectedTicket.patient_name_display || `Patient ID: ${selectedTicket.patient}`}
              </MenuItem>
            ) : (
              <MenuItem value="" disabled>{t("ct_scan.noPatientFound", "No patient found")}</MenuItem>
            )}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            required
            label={t("ct_scan.patientCtScan", "Patient CT Scan")}
            value={form.test}
            onChange={(e) => setForm({ ...form, test: e.target.value })}
            disabled={!form.ticket}
          >
            {tests.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="datetime-local"
            label={t("ct_scan.dateAndTime", "Date & Time")}
            InputLabelProps={{ shrink: true }}
            value={examDateTime}
            onChange={(e) => {
              setExamDateTime(e.target.value);
              const dt = fromDateTimeLocal(e.target.value);
              setForm({ ...form, date: dt.date, record_time: dt.time });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Button variant="outlined" component="label" fullWidth>
            {form.report_image instanceof File ? form.report_image.name : t("ct_scan.uploadPhotoReportImage", "Upload Photo / Report Image")}
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={(e) => setForm({ ...form, report_image: e.target.files?.[0] || null })}
            />
          </Button>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t("ct_scan.testAppointment", "Test Appointment")}
            value={form.test_result}
            onChange={(e) => setForm({ ...form, test_result: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}><TextField fullWidth multiline minRows={3} label={t("ct_scan.notes", "Notes")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Grid>
        {formError ? <Grid item xs={12}><Typography color="error" variant="body2">{formError}</Typography></Grid> : null}
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => { setOpenAdd(false); setEditId(null); }}>{t("ct_scan.cancel", "Cancel")}</Button>
      <Button variant="contained" onClick={handleCreate} disabled={saving || !form.patient || !form.test || !form.ticket}>{t("ct_scan.save", "Save")}</Button>
    </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteId(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          <WarningAmber sx={{ fontSize: 84, color: "#d32f2f", mb: 1 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            {t("ct_scan.confirmDeleting", "Confirm Deleting")}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {t("ct_scan.deleteWarning", "Are you sure you want to delete this item? This action cannot be undone.")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteId(null);
            }}
            sx={{ bgcolor: "#0f6074", "&:hover": { bgcolor: "#0b4f5f" } }}
          >
            {t("ct_scan.cancel", "Cancel")}
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            {t("ct_scan.delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Paper
          elevation={4}
          sx={{
            width: 315,
            borderRadius: "6px",
            bgcolor: "#e9ecef",
            overflow: "hidden",
            "@keyframes ctScanToastProgressLtr": {
              from: { transform: "scaleX(1)" },
              to: { transform: "scaleX(0)" },
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1.1 }}>
            <CheckCircleIcon
              sx={{
                color: snackbar.severity === "success" ? "#17b629" : "#d32f2f",
                mr: 1,
                fontSize: 21,
              }}
            />
            <Typography
              sx={{ color: "#5f6368", fontSize: "0.78rem", flexGrow: 1, lineHeight: 1.2 }}
            >
              {snackbar.message}
            </Typography>
            <CloseIcon
              onClick={() => setSnackbar((prev) => ({ ...prev, open: false }))}
              sx={{ color: "#8f8f8f", cursor: "pointer", fontSize: 18 }}
            />
          </Box>
          <Box
            sx={{
              height: 4,
              bgcolor: snackbar.severity === "success" ? "#22c841" : "#d32f2f",
              width: "100%",
              transformOrigin: "left",
              animation: "ctScanToastProgressLtr 3s linear forwards",
            }}
          />
        </Paper>
      </Snackbar>
    </>
  );
};

export default PatientCTScanRecordsPage;





